import React, { useState, useEffect, useRef } from "react"
import { Button, Typography, Box, LinearProgress } from "@mui/material"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import imageCompression from "browser-image-compression"
import { FFmpeg } from "@ffmpeg/ffmpeg"
import useTranslationSharie from "../Language/useTranslation"

function CaptureVideoImage(props) {
  const [file, setFile] = useState(null)
  const [fileURL, setFileURL] = useState(null)
  const [fileBase64, setFileBase64] = useState(null)
  const [isCompressing, setIsCompressing] = useState(false)
  const [isFFmpegLoaded, setIsFFmpegLoaded] = useState(false)
  const [progress, setProgress] = useState(0)
  // Initialize FFmpeg instance
  const ffmpeg = React.useMemo(() => new FFmpeg({ log: true }), [])
  const t = useTranslationSharie()
  const buttonRef = useRef(null)

  useEffect(() => {
    // מפעיל את הלחיצה אוטומטית אחרי עליית הקומפוננטה
    if (buttonRef.current && props.upload) {
      buttonRef.current.click()
    }
  }, [])
  useEffect(() => {
    // Load FFmpeg on component mount
    const loadFFmpeg = async () => {
      if (!isFFmpegLoaded) {
        await ffmpeg.load() // load FFmpeg instance
        setIsFFmpegLoaded(true)
      }
    }
    loadFFmpeg()
  }, [ffmpeg, isFFmpegLoaded])

  const handleFileChange = async (event) => {
    const uploadedFile = event.target.files[0]
    if (
      uploadedFile &&
      (uploadedFile.type.startsWith("image/") || uploadedFile.type.startsWith("video/"))
    ) {
      setFile(uploadedFile)
      setFileURL(URL.createObjectURL(uploadedFile))

      if (uploadedFile.type.startsWith("image/")) {
        await compressImage(uploadedFile)
      } else if (uploadedFile.type.startsWith("video/")) {
        await compressVideo(uploadedFile)
      }
    } else {
      alert("Please upload an image or video file.")
    }
  }

  const compressImage = async (imageFile) => {
    if (imageFile) {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      }

      try {
        const compressedFile = await imageCompression(imageFile, options)
        setFile(compressedFile)
        setFileURL(URL.createObjectURL(compressedFile))
        convertToBase64(compressedFile)
      } catch (error) {
        console.error("Error compressing image:", error)
      }
    }
  }

  const compressVideo = async (videoFile) => {
    setIsCompressing(true)
    props.setIsCompressing(true)
    try {
      if (!isFFmpegLoaded) {
        alert("FFmpeg is not loaded yet. Please wait.")
        return
      }

      const videoData = await videoFile.arrayBuffer()
      await ffmpeg.writeFile("input.mp4", new Uint8Array(videoData))

      ffmpeg.on("progress", ({ progress, time }) => {
        setProgress(progress * 100)
      })

      await ffmpeg.exec([
        "-i",
        "input.mp4",
        "-report",
        "-vcodec",
        "libx264",
        "-crf",
        "28",
        "-preset",
        "veryfast", // Faster compression change to fast if compression is weak
        "output.mp4",
      ])

      const compressedData = await ffmpeg.readFile("output.mp4")

      const compressedBlob = new Blob([compressedData.buffer], { type: "video/mp4" })
      const compressedURL = URL.createObjectURL(compressedBlob)

      setFileURL(compressedURL)
      setFile(new File([compressedBlob], "compressed_video.mp4", { type: "video/mp4" }))

      convertToBase64(new File([compressedBlob], "compressed_video.mp4", { type: "video/mp4" }))
    } catch (error) {
      console.error("Error compressing video:", error)
      alert("An error occurred while compressing the video.")
    } finally {
      setIsCompressing(false)
      props.setIsCompressing(false)
    }
  }

  const convertToBase64 = (file) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)

    reader.onloadend = () => {
      let base64String = reader.result

      if (file.type.startsWith("video/")) {
        const videoPrefix = `data:${file.type};base64,`
        if (!base64String.startsWith(videoPrefix)) {
          base64String = videoPrefix + base64String.split(",")[1]
        }
      }

      props.setImage(base64String)
      setFileBase64(base64String)
    }

    reader.onerror = (error) => {
      console.error("Error converting file to Base64:", error)
    }
  }

  const handleUploadClick = () => {
    document.getElementById("file-input").click()
  }

  return (
    <>
      {/* <Typography variant='h6' gutterBottom>
        Upload Image/Video File
      </Typography> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 2,
          border: "1px solid #ccc",
          borderRadius: 2,
          width: 300,
          height: 250,
          mt: 4,
          textAlign: "center",
        }}
      >
        {fileURL && file && (
          <Box sx={{ mb: 2, width: "100%" }}>
            {file.type.startsWith("image/") ? (
              <img
                src={fileURL}
                alt='Preview'
                style={{ width: "100%", height: 250, borderRadius: 8 }}
              />
            ) : (
              <video controls style={{ width: "100%", borderRadius: 8 }}>
                <source src={fileURL} type={file.type} />
                Your browser does not support the video tag.
              </video>
            )}
          </Box>
        )}

        {isCompressing && (
          <Box key={progress}>
            <Typography variant='body2' color='textSecondary'>
              Compressing video...
            </Typography>
            <LinearProgress
              variant='determinate'
              value={progress}
              sx={{ width: "300px", height: "30px" }}
            />
          </Box>
        )}
        <input
          id='file-input'
          type='file'
          accept='image/*,video/*'
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        <Button
          ref={buttonRef}
          variant='contained'
          onClick={handleUploadClick}
          startIcon={<UploadFileIcon />}
          sx={{ mt: 2 }}
        >
          {t("Choose File")}
        </Button>

        {file && (
          <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
            Selected: {file.name}
          </Typography>
        )}
      </Box>
    </>
  )
}

export default CaptureVideoImage
